import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { mailboxSubscriptionService } from '@/lib/mailbox-subscription-service'

export interface Mailbox {
  id: string
  email: string
  domain: string
  from_name: string
  provider: string
  subdomain?: string
  smartlead_id?: number
  status?: string
  daily_limit?: number
  current_daily_sent?: number
  signature?: string
  smtp_success?: boolean
  imap_success?: boolean
  smtp_host?: string
  smtp_port?: number
  imap_host?: string
  imap_port?: number
  imap_username?: string
  custom_tracking_domain?: string
  client_id?: number
  type?: string
  username?: string
  smtp_port_type?: string
  imap_port_type?: string
  message_per_day?: number
  different_reply_to_address?: string
  is_different_imap_account?: boolean
  smtp_failure_error?: string
  imap_failure_error?: string
  daily_sent_count?: number
  warmup_status?: string
  warmup_sent_total?: number
  warmup_spam_total?: number
  warmup_reputation?: string
  api_sync_status?: string
  api_sync_error?: string
  last_api_synced_at?: string
  sync_attempted_at?: string
  sync_attempt_count?: number
  created_at?: string
  updated_at?: string
  status_updated_at?: string
  deactivated_at?: string
  smartlead_removed_at?: string
  deactivation_reason?: string
  // New fields from MailboxEditDialog
  app_password?: string
  email_password?: string
  mailbox_cost?: string
  warmup_enabled_date?: string
  minimum_time_gap?: number
  registration_date?: string
  purchase_date?: string
  // Sub-status and batch number fields
  sub_status?: string
  batch_number?: number
  notes?: string

  // Tags (from SmartLead sync)
  tags?: Array<{id: number, name: string}> | null

  // Provider info (from domains table join)
  mailbox_provider?: string
  email_provider?: string

  // Campaign Metrics (from obsync_dynamic_reports backfill)
  total_emails_sent?: number
  total_email_replies?: number
  total_email_bounces?: number

  // Smartlead Sync Fields (from mailbox_sequencer_connections)
  bounce_rate?: number
  spam_score?: number
  connection_health?: string
  sequencer_platform?: string
  sequencer_mailbox_id?: string
  last_connection_check?: string
  connection_error?: string
}

export function useMailboxes(filterClientId?: number) {
  const queryClient = useQueryClient()

  // Use centralized subscription service
  useEffect(() => {
    mailboxSubscriptionService.setQueryClient(queryClient)
    mailboxSubscriptionService.subscribe('useMailboxes')

    return () => {
      mailboxSubscriptionService.unsubscribe('useMailboxes')
    }
  }, [queryClient])

  return useQuery({
    queryKey: ['mailboxes', filterClientId ?? 'all'],
    queryFn: async (): Promise<Mailbox[]> => {
      // Paginate to bypass Supabase's default 1000 row limit
      const allData: any[] = []
      const pageSize = 1000
      let offset = 0

      while (true) {
        let query = supabase
          .from('mailboxes')
          .select(`
            *,
            mailbox_sequencer_connections!left(
              bounce_rate,
              spam_score,
              connection_health,
              sequencer_platform,
              sequencer_mailbox_id,
              last_connection_check,
              connection_error
            )
          `)

        // Filter by client_id (SmartLead integer ID) when scoped
        if (filterClientId !== undefined) {
          query = query.eq('client_id', filterClientId)
        }

        const { data, error } = await query
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1)

        if (error) {
          console.error('Error fetching mailboxes:', error)
          throw error
        }

        if (!data || data.length === 0) break
        allData.push(...data)
        if (data.length < pageSize) break
        offset += pageSize
      }

      // Fetch domain provider info for enrichment
      const { data: domainProviders } = await supabase
        .from('domains')
        .select('domain, mailbox_provider, email_provider')

      const domainProviderMap = new Map<string, { mailbox_provider?: string; email_provider?: string }>()
      if (domainProviders) {
        domainProviders.forEach(d => {
          domainProviderMap.set(d.domain, { mailbox_provider: d.mailbox_provider, email_provider: d.email_provider })
        })
      }

      // Transform data to flatten Smartlead sync fields and enrich with domain providers
      const transformedData = allData.map(mailbox => {
        const connection = mailbox.mailbox_sequencer_connections?.[0] // Get first connection
        const domainInfo = domainProviderMap.get(mailbox.domain)
        return {
          ...mailbox,
          // Flatten Smartlead sync fields from the joined table
          bounce_rate: connection?.bounce_rate,
          spam_score: connection?.spam_score,
          connection_health: connection?.connection_health,
          sequencer_platform: connection?.sequencer_platform,
          sequencer_mailbox_id: connection?.sequencer_mailbox_id,
          last_connection_check: connection?.last_connection_check,
          connection_error: connection?.connection_error,
          // Enrich with domain provider info
          mailbox_provider: domainInfo?.mailbox_provider,
          email_provider: domainInfo?.email_provider,
          // Remove the nested object
          mailbox_sequencer_connections: undefined
        }
      })

      return transformedData as Mailbox[]
    },
    staleTime: 30 * 1000, // Reduced to 30 seconds since we have real-time updates
  })
}
